import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Loader2, Plus, Trash2, Save, RotateCcw, Sparkles, GripVertical } from "lucide-react";
import { toast } from "sonner";
import { adminGetCatalog, adminPutCatalog, adminResetCatalog } from "@/lib/api";

const MODELS = ["free", "buy", "embed", "subscribe"];
const MODEL_LABEL = { free: "Free", buy: "Buy once", embed: "Embed", subscribe: "Subscribe" };
const input = "w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:border-brand outline-none";

const ModelToggles = ({ value = [], onChange }) => (
  <div className="flex flex-wrap gap-1.5">
    {MODELS.map((m) => {
      const on = value.includes(m);
      return (
        <button key={m} type="button" onClick={() => onChange(on ? value.filter((x) => x !== m) : [...value, m])}
          className={`text-[11px] font-semibold px-2.5 py-1 rounded-full border transition-colors ${on ? "bg-brand text-white border-brand" : "bg-white text-slate-500 border-slate-200"}`}>
          {MODEL_LABEL[m]}
        </button>
      );
    })}
  </div>
);

const Flag = ({ label, checked, onChange }) => (
  <label className="inline-flex items-center gap-1.5 text-xs text-slate-600 cursor-pointer">
    <input type="checkbox" checked={!!checked} onChange={(e) => onChange(e.target.checked)} className="accent-brand" /> {label}
  </label>
);

export default function AdminToolsCatalog() {
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { adminGetCatalog().then(setData).catch(() => { toast.error("Failed to load catalog"); }); }, []);

  const setSection = (i, patch) => setData((d) => ({ ...d, sections: d.sections.map((s, si) => si === i ? { ...s, ...patch } : s) }));
  const setTool = (si, ti, patch) => setSection(si, { tools: data.sections[si].tools.map((t, i) => i === ti ? { ...t, ...patch } : t) });
  const addTool = (si) => setSection(si, { tools: [...data.sections[si].tools, { name: "New tool", desc: "", models: ["subscribe"] }] });
  const delTool = (si, ti) => setSection(si, { tools: data.sections[si].tools.filter((_, i) => i !== ti) });

  const setPackage = (i, patch) => setData((d) => ({ ...d, packages: d.packages.map((p, pi) => pi === i ? { ...p, ...patch } : p) }));
  const addPackage = () => setData((d) => ({ ...d, packages: [...d.packages, { name: "New plan", tagline: "", priceFrom: "Get a quote", bullets: [], models: ["subscribe"] }] }));
  const delPackage = (i) => setData((d) => ({ ...d, packages: d.packages.filter((_, pi) => pi !== i) }));

  const save = async () => {
    setSaving(true);
    try { await adminPutCatalog({ sections: data.sections, packages: data.packages }); toast.success("Catalog saved — live on /tools"); }
    catch { toast.error("Save failed"); } finally { setSaving(false); }
  };
  const reset = async () => {
    if (!window.confirm("Restore the built-in default catalog? This overwrites your edits.")) return;
    try { const d = await adminResetCatalog(); setData(d); toast.success("Catalog reset to default"); } catch { toast.error("Reset failed"); }
  };

  if (!data) return <main className="min-h-screen bg-slate-50 grid place-items-center"><Loader2 className="animate-spin text-brand" size={26} /></main>;

  return (
    <main className="min-h-screen bg-slate-50 pb-24" data-testid="admin-tools-catalog">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <Link to="/admin" className="text-slate-500 hover:text-brand text-sm flex items-center gap-1.5 mb-3"><ArrowLeft size={14} /> Back to console</Link>
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-2"><Sparkles size={22} className="text-brand" /><h1 className="font-heading text-3xl font-extrabold tracking-tight text-slate-900">Tools & Packages Editor</h1></div>
          <div className="flex items-center gap-2">
            <button onClick={reset} data-testid="catalog-reset" className="inline-flex items-center gap-1.5 btn-outline rounded-lg px-4 py-2.5 text-sm"><RotateCcw size={15} /> Reset</button>
            <a href="/tools" target="_blank" rel="noopener noreferrer" className="text-sm text-slate-500 hover:text-brand px-2">Preview /tools ↗</a>
            <button onClick={save} disabled={saving} data-testid="catalog-save" className="inline-flex items-center gap-1.5 bg-brand text-white rounded-lg px-5 py-2.5 text-sm font-semibold disabled:opacity-60">{saving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} Save</button>
          </div>
        </div>
        <p className="text-slate-500 mt-1 text-sm">Edit sections, tools, delivery models and prices. Changes go live on the public /tools page after saving.</p>

        {/* Sections */}
        {data.sections.map((s, si) => (
          <div key={s.id || si} className="bg-white border border-slate-200 rounded-2xl card-shadow p-5 mt-6" data-testid={`catalog-section-${s.id || si}`}>
            <div className="flex items-center gap-2 mb-3">
              <GripVertical size={16} className="text-slate-300" />
              <input value={s.title} onChange={(e) => setSection(si, { title: e.target.value })} className="font-heading font-bold text-lg text-slate-900 border-b border-transparent hover:border-slate-200 focus:border-brand outline-none flex-1" data-testid={`section-title-${si}`} />
            </div>
            <input value={s.blurb || ""} onChange={(e) => setSection(si, { blurb: e.target.value })} placeholder="Section blurb" className={`${input} mb-4`} />
            <div className="space-y-3">
              {s.tools.map((t, ti) => (
                <div key={ti} className="border border-slate-100 rounded-xl p-3 bg-slate-50/50" data-testid={`tool-editor-${si}-${ti}`}>
                  <div className="flex items-start gap-2">
                    <input value={t.name} onChange={(e) => setTool(si, ti, { name: e.target.value })} placeholder="Tool name" className={`${input} font-semibold`} />
                    <button onClick={() => delTool(si, ti)} className="text-slate-300 hover:text-red-500 p-2 shrink-0" data-testid={`tool-delete-${si}-${ti}`}><Trash2 size={16} /></button>
                  </div>
                  <textarea value={t.desc || ""} onChange={(e) => setTool(si, ti, { desc: e.target.value })} placeholder="What it does" rows={2} className={`${input} mt-2 resize-none`} />
                  <div className="grid sm:grid-cols-2 gap-2 mt-2">
                    <input value={t.priceFrom || ""} onChange={(e) => setTool(si, ti, { priceFrom: e.target.value })} placeholder="Indicative price (optional)" className={input} />
                    <input value={t.to || ""} onChange={(e) => setTool(si, ti, { to: e.target.value })} placeholder="Link path e.g. /demos (optional)" className={input} />
                  </div>
                  <div className="flex flex-wrap items-center justify-between gap-3 mt-3">
                    <ModelToggles value={t.models} onChange={(models) => setTool(si, ti, { models })} />
                    <div className="flex items-center gap-3">
                      <Flag label="New" checked={t.isNew} onChange={(v) => setTool(si, ti, { isNew: v })} />
                      <Flag label="Soon" checked={t.comingSoon} onChange={(v) => setTool(si, ti, { comingSoon: v })} />
                      <Flag label="Highlight" checked={t.highlight} onChange={(v) => setTool(si, ti, { highlight: v })} />
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <button onClick={() => addTool(si)} data-testid={`add-tool-${si}`} className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-brand hover:gap-2 transition-all"><Plus size={15} /> Add tool</button>
          </div>
        ))}

        {/* Packages */}
        <h2 className="font-heading text-2xl font-extrabold tracking-tight text-slate-900 mt-10 mb-3">Managed Plans</h2>
        <div className="grid md:grid-cols-2 gap-4">
          {data.packages.map((p, i) => (
            <div key={i} className="bg-white border border-slate-200 rounded-2xl card-shadow p-4" data-testid={`package-editor-${i}`}>
              <div className="flex items-start gap-2">
                <input value={p.name} onChange={(e) => setPackage(i, { name: e.target.value })} placeholder="Plan name" className={`${input} font-semibold`} />
                <button onClick={() => delPackage(i)} className="text-slate-300 hover:text-red-500 p-2 shrink-0"><Trash2 size={16} /></button>
              </div>
              <div className="grid grid-cols-2 gap-2 mt-2">
                <input value={p.tagline || ""} onChange={(e) => setPackage(i, { tagline: e.target.value })} placeholder="Tagline" className={input} />
                <input value={p.priceFrom || ""} onChange={(e) => setPackage(i, { priceFrom: e.target.value })} placeholder="Price e.g. from $49/mo" className={input} />
              </div>
              <textarea value={(p.bullets || []).join("\n")} onChange={(e) => setPackage(i, { bullets: e.target.value.split("\n").filter(Boolean) })} placeholder="One feature per line" rows={4} className={`${input} mt-2 resize-none`} />
              <div className="flex items-center justify-between gap-3 mt-2">
                <ModelToggles value={p.models} onChange={(models) => setPackage(i, { models })} />
                <Flag label="Popular" checked={p.popular} onChange={(v) => setPackage(i, { popular: v })} />
              </div>
            </div>
          ))}
        </div>
        <button onClick={addPackage} data-testid="add-package" className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-brand"><Plus size={15} /> Add plan</button>
      </div>
    </main>
  );
}
